// frontend/src/contexts/AuthContext.tsx

import React, { createContext, useContext, useState, useEffect } from 'react';
import api from '../services/api';

export interface User {
  id: number;
  name: string;
  email: string;
  phone?: string;
  company?: string;
  loginCount?: number;
  createdAt?: string;
}

export interface AuthContextData {
  user: User | null;
  loading: boolean;
  isAuthenticated: boolean;
  login: (email: string, password: string) => Promise<void>;
  logout: () => void;
  register: (data: RegisterData) => Promise<void>;
  updateUser: (user: User) => void;
  selectedTraceId: string | null;
  selectedDataset: string | null;
  selectTrace: (traceId: string, datasetName: string) => void;
  clearTrace: () => void;
}

export interface RegisterData {
  name: string;
  email: string;
  password: string;
  phone?: string;
  company?: string;
}

const AuthContext = createContext<AuthContextData>({} as AuthContextData);

export const AuthProvider: React.FC<{ children: React.ReactNode }> = ({ children }) => {
  const [user, setUser] = useState<User | null>(null);
  const [loading, setLoading] = useState(true);
  const [selectedTraceId, setSelectedTraceId] = useState<string | null>(null);
  const [selectedDataset, setSelectedDataset] = useState<string | null>(null);

  useEffect(() => {
    const loadUser = async () => {
      const token = localStorage.getItem('token');
      const savedUser = localStorage.getItem('user');

      if (token && savedUser) {
        try {
          const userData = JSON.parse(savedUser);
          setUser(userData);
          api.defaults.headers.common['Authorization'] = `Bearer ${token}`;
        } catch {
          localStorage.removeItem('token');
          localStorage.removeItem('user');
        }
      }
      setLoading(false);
    };

    loadUser();

    const savedTrace = localStorage.getItem('autosage_trace_id');
    const savedDataset = localStorage.getItem('autosage_dataset');
    if (savedTrace) setSelectedTraceId(savedTrace);
    if (savedDataset) setSelectedDataset(savedDataset);
  }, []);

  const login = async (email: string, password: string) => {
    try {
      const response = await api.post('/auth/login', { email, password });

      if (response.data.success) {
        const { token, user } = response.data;
        localStorage.setItem('token', token);
        localStorage.setItem('user', JSON.stringify(user));
        api.defaults.headers.common['Authorization'] = `Bearer ${token}`;
        setUser(user);
      } else {
        throw new Error(response.data.message || 'Erro ao fazer login');
      }
    } catch (error: any) {
      console.error('❌ Erro no login:', error);
      throw error;
    }
  };

  const logout = () => {
    localStorage.removeItem('token');
    localStorage.removeItem('user');
    localStorage.removeItem('autosage_trace_id');
    localStorage.removeItem('autosage_dataset');
    delete api.defaults.headers.common['Authorization'];
    setUser(null);
    setSelectedTraceId(null);
    setSelectedDataset(null);
  };

  // ⭐ CORRIGIDO — salva token e user (login automático após cadastro)
  const register = async (data: RegisterData) => {
    const response = await api.post('/auth/register', data);

    if (!response.data.success) {
      throw new Error(response.data.message || 'Erro ao registrar');
    }

    const { token, user } = response.data;

    if (!token || !user) {
      throw new Error('Resposta do servidor inválida: token ou usuário ausente');
    }

    localStorage.setItem('token', token);
    localStorage.setItem('user', JSON.stringify(user));
    api.defaults.headers.common['Authorization'] = `Bearer ${token}`;
    setUser(user);
  };

  const updateUser = (updatedUser: User) => {
    setUser(updatedUser);
    localStorage.setItem('user', JSON.stringify(updatedUser));
  };

  const selectTrace = (traceId: string, datasetName: string) => {
    setSelectedTraceId(traceId);
    setSelectedDataset(datasetName);
    localStorage.setItem('autosage_trace_id', traceId);
    localStorage.setItem('autosage_dataset', datasetName);
  };

  const clearTrace = () => {
    setSelectedTraceId(null);
    setSelectedDataset(null);
    localStorage.removeItem('autosage_trace_id');
    localStorage.removeItem('autosage_dataset');
  };

  return (
    <AuthContext.Provider value={{
      user,
      loading,
      isAuthenticated: !!user,
      login,
      logout,
      register,
      updateUser,
      selectedTraceId,
      selectedDataset,
      selectTrace,
      clearTrace,
    }}>
      {children}
    </AuthContext.Provider>
  );
};

export const useAuth = () => {
  const context = useContext(AuthContext);
  if (!context) {
    throw new Error('useAuth must be used within an AuthProvider');
  }
  return context;
};